<script setup lang="ts">
  import {
    isStakeholderOtherParty,
    isStakeholderVendor,
    isStakeholderClient,
    type Stakeholder,
    testIds
  } from '@gat-ship/types'
  import { IconLoading } from '@gat-ship/icons'
  import { useStakeholderContext } from '../../composables/useStakeholder'
  import { useStakeholderPermissions } from '@gat-ship/stakeholders'
  import {
    useAppLoading,
    type ButtonProps,
    Button
  } from '@gat-ship/vue-components'
  import { computed, ref } from 'vue'
  import { text } from '@gat-ship/stakeholders'
  import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@gat-ship/vue-components'

  const props = defineProps<{ stakeholder: Stakeholder }>()

  const service = useStakeholderContext()

  const emit = defineEmits<{
    (e: 'saving'): void
    (e: 'deleted'): void
  }>()

  const { isLoading: appIsLoading } = useAppLoading()

  const isLoading = computed(() => appIsLoading(props.stakeholder.id))
  const showConfirm = ref(false)
  const confirmText = computed(
    () =>
      text.groups.visitAddress(props.stakeholder.stakeholderType).confirmDialogs
        .removeAddress
  )
  const btnConfig = computed(
    () =>
      text.groups.visitAddress(props.stakeholder.stakeholderType).buttons
        .removeAddress as {
        label: string
        variant?: ButtonProps['variant']
        size?: ButtonProps['size']
        icon?: unknown
        class?: string
      }
  )

  async function onDeleteVisitAddress(): Promise<void> {
    showConfirm.value = false
    emit('saving')
    await service?.commands.changeVisitAddress(props.stakeholder.id, {
      visitAddress: null
    })
    emit('deleted')
  }

  const { canModify } = useStakeholderPermissions()

  /** The type guards narrow the union so `visitAddress` is reachable. */
  function hasVisitAddress(): boolean {
    const stakeholder = props.stakeholder
    return (
      (isStakeholderOtherParty(stakeholder) ||
        isStakeholderVendor(stakeholder) ||
        isStakeholderClient(stakeholder)) &&
      stakeholder.visitAddress !== null
    )
  }

  function showButton() {
    if (!canModify(props.stakeholder)) {
      return false
    }
    return (
      hasVisitAddress() || Boolean(props.stakeholder.visitAddressSameAsPostal)
    )
  }
</script>

<template>
  <Button
    v-if="showButton()"
    :variant="btnConfig.variant"
    :size="btnConfig.size"
    :class="btnConfig.class"
    :disabled="isLoading"
    :data-testid="testIds.stakeholders.actions.removeVisitAddress"
    @click="showConfirm = true"
  >
    <IconLoading v-if="isLoading" class="size-4 shrink-0" />
    <component :is="btnConfig.icon" v-else-if="btnConfig.icon" class="size-4" />
    {{ btnConfig.label }}
  </Button>
  <AlertDialog v-model:open="showConfirm">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ confirmText.title }}</AlertDialogTitle>
        <AlertDialogDescription class="whitespace-pre-line">
          {{ confirmText.message(props.stakeholder.name) }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <Button variant="outline" @click="onDeleteVisitAddress()">
          Confirm
        </Button>
        <Button variant="outline" @click="showConfirm = false"> Cancel </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
